// ─── ErrorBoundary.jsx ────────────────────────────────────────────────────────
// Per-module React error boundary. Prevents one page crash from killing the app.
// Import as: import ErrorBoundary from "./ErrorBoundary";
//
// Usage (wrap every page in App.jsx pages object):
//   <ErrorBoundary name="Case Management"><CaseManagement ... /></ErrorBoundary>

import React from "react";
import { withTranslation } from 'react-i18next';
import { ThemeContext } from "./theme";

class ErrorBoundaryClass extends React.Component {
  static contextType = ThemeContext;

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error(`[ErrorBoundary:${this.props.name || "unknown"}]`, error, info?.componentStack);
  }

  render() {
    const C = this.context;
    const { t } = this.props;
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: "60px 24px", textAlign: "center",
        fontFamily: "'DM Mono', monospace" }}>
        <div style={{ fontSize: 36, marginBottom: 16, color: C.warn, opacity: 0.6 }}>⚠</div>
        <div style={{ color: C.text, fontSize: 17, fontWeight: 700,
          fontFamily: "'Syne', sans-serif", marginBottom: 10 }}>
          {this.props.name ? `${this.props.name} ${t('errorBoundary.title')}` : t('errorBoundary.title')}
        </div>
        <div style={{ color: C.textDim, fontSize: 12, maxWidth: 420, lineHeight: 1.7,
          marginBottom: 24, fontFamily: "'DM Mono', monospace" }}>
          {this.state.error?.message || t('errorBoundary.body')}
        </div>
        <button
          onClick={() => this.setState({ hasError: false, error: null })}
          style={{ background: C.accent, border: "none", borderRadius: 8, color: C.bg,
            padding: "10px 24px", cursor: "pointer", fontSize: 13, fontWeight: 600,
            fontFamily: "'DM Mono', monospace" }}
        >
          {t('errorBoundary.reload')}
        </button>
      </div>
    );
  }
}

export default withTranslation()(ErrorBoundaryClass);